import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [formData, setFormData] = useState({
    fullName: '',
    organization: '',
    email: '',
    phone: '',
    inquiry: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formData.message.length > 500) {
      alert('Message must be 500 characters or less');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const response = await fetch(`${supabaseUrl}/functions/v1/contact-form-handler`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fullName: formData.fullName,
          organization: formData.organization,
          email: formData.email,
          phone: formData.phone,
          inquiryType: formData.inquiry,
          message: formData.message
        })
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          fullName: '',
          organization: '',
          email: '',
          phone: '',
          inquiry: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white font-['Inter',sans-serif]">
      {/* Sticky Navigation */}
      <nav className="sticky top-0 left-0 right-0 z-50 bg-white shadow-sm">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-3 md:py-0 md:h-24 lg:h-28">
          {/* Mobile Layout */}
          <div className="md:hidden">
            <div className="flex items-center justify-between mb-3">
              <Link to="/" className="flex items-center cursor-pointer">
                <img 
                  src="https://static.readdy.ai/image/a2144585b096b0c5bc2cbabd4c2b6ea8/e662b63e618aedfff5c098ee5ba35e0a.png" 
                  alt="DataVerge Solutions Logo" 
                  className="h-12 w-auto"
                />
              </Link>
              <a href="#contact" className="bg-[#FF6B35] text-white px-4 py-2 rounded-lg hover:bg-[#000080] transition-all flex items-center gap-2 whitespace-nowrap shadow-lg hover:shadow-xl cursor-pointer text-xs">
                Get Started
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>
            <div className="flex items-center justify-center gap-3">
              <a href="#solutions" className="text-[#000080] hover:text-[#FF6B35] transition-colors cursor-pointer text-xs whitespace-nowrap scroll-smooth">Solutions</a>
              <a href="#insights" className="text-[#000080] hover:text-[#FF6B35] transition-colors cursor-pointer text-xs whitespace-nowrap scroll-smooth">Insights</a>
              <a href="#about" className="text-[#000080] hover:text-[#FF6B35] transition-colors cursor-pointer text-xs whitespace-nowrap scroll-smooth">About</a>
              <a href="#contact" className="text-[#000080] hover:text-[#FF6B35] transition-colors cursor-pointer text-xs whitespace-nowrap scroll-smooth">Contact</a>
            </div>
          </div>

          {/* Desktop Layout */}
          <div className="hidden md:flex items-center justify-between h-full">
            <div className="flex items-center">
              <Link to="/" className="flex items-center gap-2 md:gap-3 cursor-pointer">
                <img 
                  src="https://static.readdy.ai/image/a2144585b096b0c5bc2cbabd4c2b6ea8/e662b63e618aedfff5c098ee5ba35e0a.png" 
                  alt="DataVerge Solutions Logo" 
                  className="h-24 lg:h-32 w-auto"
                />
              </Link>
            </div>
            
            <div className="flex items-center gap-4 lg:gap-10">
              <a href="#solutions" className="text-[#000080] hover:text-[#FF6B35] transition-colors cursor-pointer text-sm lg:text-base whitespace-nowrap scroll-smooth">Solutions</a>
              <a href="#insights" className="text-[#000080] hover:text-[#FF6B35] transition-colors cursor-pointer text-sm lg:text-base whitespace-nowrap scroll-smooth">Insights</a>
              <a href="#about" className="text-[#000080] hover:text-[#FF6B35] transition-colors cursor-pointer text-sm lg:text-base whitespace-nowrap scroll-smooth">About</a>
              <a href="#contact" className="text-[#000080] hover:text-[#FF6B35] transition-colors cursor-pointer text-sm lg:text-base whitespace-nowrap scroll-smooth">Contact</a>
            </div>

            <a href="#contact" className="bg-[#FF6B35] text-white px-6 lg:px-8 py-3 rounded-lg hover:bg-[#000080] transition-all flex items-center gap-2 whitespace-nowrap shadow-lg hover:shadow-xl cursor-pointer text-sm lg:text-base">
              Get Started
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-10 md:pt-16 lg:pt-20 min-h-[600px] md:min-h-screen flex items-center bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-10 md:py-16 lg:py-20 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 md:gap-12 items-center">
            <div className="lg:col-span-3">
              <div className="text-[#FF6B35] text-xs md:text-sm font-semibold tracking-wider mb-4 md:mb-8">OPERATIONAL INTELLIGENCE</div>
              <h1 className="text-3xl md:text-5xl lg:text-7xl font-bold text-[#000080] leading-tight mb-4 md:mb-6 lg:mb-8">
                Digital Infrastructure for Essential Industries
              </h1>
              <p className="text-base md:text-xl lg:text-2xl text-[#708090] leading-relaxed mb-8 md:mb-12 lg:mb-16 max-w-2xl font-light">
                DataVerge Solutions turns fragmented data into financial certainty. We provide operational intelligence for the underserved sectors that power our communities.
              </p>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 md:gap-6">
                <a href="#solutions" className="bg-[#FF6B35] text-white px-6 md:px-8 lg:px-10 py-3 md:py-4 rounded-lg hover:bg-[#000080] transition-all text-sm md:text-base font-medium whitespace-nowrap cursor-pointer shadow-lg hover:shadow-xl text-center">
                  Explore Our Solutions
                </a>
                <a href="#contact" className="border-2 border-[#FF6B35] text-[#FF6B35] px-6 md:px-8 lg:px-10 py-3 md:py-4 rounded-lg hover:bg-[#FF6B35] hover:text-white transition-all text-sm md:text-base font-medium whitespace-nowrap cursor-pointer text-center">
                  Contact Our Team
                </a>
              </div>
            </div>
            
            <div className="lg:col-span-2 relative h-[300px] md:h-[400px] lg:h-[600px]">
              <div className="absolute inset-0 flex items-center justify-center">
                <img 
                  src="https://public.readdy.ai/ai/img_res/5c9a7f37c43a81834eaeb004de95f4b5.jpg"
                  alt="Data Network Visualization"
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bridge Section */}
      <section className="bg-gradient-to-b from-white to-[#F5F6F7] py-12 md:py-16 lg:py-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20">
          <div className="relative h-[250px] md:h-[350px] lg:h-[400px] rounded-2xl md:rounded-3xl overflow-hidden shadow-2xl">
            <img 
              src="https://readdy.ai/api/search-image?query=Modern%20architectural%20bridge%20spanning%20across%20water%20at%20golden%20hour%20with%20strong%20navy%20blue%20steel%20structure%20and%20bright%20vibrant%20orange%20accent%20lighting%2C%20symbolizing%20connection%20and%20stability%2C%20professional%20corporate%20photography%20style%2C%20clean%20lines%20representing%20data%20flow%20and%20integration%2C%20warm%20sunlight%20creating%20sense%20of%20trust%20and%20reliability%2C%20architectural%20excellence%20showing%20strength%20and%20authority%2C%20inspiring%20perspective%20emphasizing%20progress%20and%20innovation%2C%20high%20quality%20business%20imagery&width=1400&height=400&seq=bridge-connection-hero&orientation=landscape"
              alt="Bridge symbolizing connection and stability"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#000080]/80 via-[#000080]/60 to-transparent"></div>
            <div className="absolute inset-0 flex items-center">
              <div className="max-w-2xl px-6 md:px-12 lg:px-16">
                <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-white mb-3 md:mb-4 lg:mb-6">
                  Building Bridges to Financial Certainty
                </h2>
                <p className="text-sm md:text-lg lg:text-xl text-white/90 leading-relaxed">
                  We connect fragmented systems, bridge data gaps, and create pathways to operational excellence. Your success is our foundation.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Statement Section */}
      <section className="bg-[#F5F6F7] py-16 md:py-24 lg:py-32">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 lg:px-20">
          <div className="text-center mb-8 md:mb-12">
            <div className="inline-block bg-[#FF6B35] text-white px-4 md:px-6 py-2 rounded-lg text-xs md:text-sm font-semibold mb-6 md:mb-10">
              THE CHALLENGE
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#000080] mb-4 md:mb-6 lg:mb-8">
              Data Blindness in Essential Industries
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-12 md:mb-16 lg:mb-20">
            <div className="bg-white rounded-2xl p-6 md:p-8 shadow-lg hover:shadow-xl transition-shadow border-t-4 border-[#FF6B35]">
              <div className="w-12 h-12 flex items-center justify-center mb-4 md:mb-6">
                <i className="ri-alert-line text-3xl md:text-4xl text-[#FF6B35]"></i>
              </div>
              <h3 className="text-lg md:text-xl font-bold text-[#000080] mb-3 md:mb-4">Fragmented Systems</h3>
              <p className="text-sm md:text-base text-[#708090] leading-relaxed">
                Multiple disconnected platforms create data silos that prevent organizations from seeing the complete operational picture, leading to inefficiencies and missed opportunities.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-6 md:p-8 shadow-lg hover:shadow-xl transition-shadow border-t-4 border-[#FF6B35]">
              <div className="w-12 h-12 flex items-center justify-center mb-4 md:mb-6">
                <i className="ri-money-dollar-circle-line text-3xl md:text-4xl text-[#FF6B35]"></i>
              </div>
              <h3 className="text-lg md:text-xl font-bold text-[#000080] mb-3 md:mb-4">Revenue Leakage</h3>
              <p className="text-sm md:text-base text-[#708090] leading-relaxed">
                Without unified visibility into billing cycles and payment processes, organizations lose significant revenue through unbilled services and delayed collections.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-6 md:p-8 shadow-lg hover:shadow-xl transition-shadow border-t-4 border-[#FF6B35]">
              <div className="w-12 h-12 flex items-center justify-center mb-4 md:mb-6">
                <i className="ri-shield-cross-line text-3xl md:text-4xl text-[#FF6B35]"></i>
              </div>
              <h3 className="text-lg md:text-xl font-bold text-[#000080] mb-3 md:mb-4">Compliance Risk</h3>
              <p className="text-sm md:text-base text-[#708090] leading-relaxed">
                Manual processes and disconnected systems increase the likelihood of regulatory violations, exposing organizations to penalties and reputational damage.
              </p>
            </div>
          </div>

          <div className="text-center">
            <p className="text-xl md:text-2xl lg:text-3xl italic text-[#000080] mb-4 md:mb-6 font-light">
              "You cannot manage what you cannot measure."
            </p>
            <div className="w-24 md:w-32 h-0.5 bg-[#FF6B35] mx-auto"></div>
          </div>
        </div>
      </section>

      {/* Core Focus Areas */}
      <section id="solutions" className="bg-white py-16 md:py-24 lg:py-32 scroll-mt-20 md:scroll-mt-24 lg:scroll-mt-28">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 lg:px-20">
          <div className="text-center mb-12 md:mb-16 lg:mb-20">
            <div className="text-[#FF6B35] text-xs md:text-sm font-semibold mb-3 md:mb-4">CORE FOCUS</div>
            <h2 className="text-3xl md:text-5xl lg:text-6xl font-bold text-[#000080]">Our Three Pillars of Impact</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 lg:gap-10">
            <div className="border-l-4 border-[#FF6B35] pl-6 md:pl-8 lg:pl-10 pr-4 md:pr-6 py-6 md:py-8 lg:py-10 hover:shadow-xl transition-all hover:-translate-y-2 bg-white rounded-r-xl">
              <div className="w-12 h-12 md:w-16 md:h-16 flex items-center justify-center mb-4 md:mb-6">
                <i className="ri-money-dollar-circle-line text-4xl md:text-5xl lg:text-6xl text-[#FF6B35]"></i>
              </div>
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-full border-2 border-[#FF6B35] flex items-center justify-center mb-3 md:mb-4">
                <span className="text-[#FF6B35] font-bold text-sm md:text-base">01</span>
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-[#000080] mb-2 md:mb-3">Financial Integrity</h3>
              <div className="text-xs md:text-sm text-[#708090] uppercase tracking-wider mb-4 md:mb-6 font-semibold">ENSURING SUSTAINABILITY</div>
              <p className="text-sm md:text-base text-[#708090] leading-relaxed mb-6 md:mb-8">
                We help mission-driven organizations capture and retain every dollar of funding they are owed. By identifying bottlenecks in the revenue cycle and reconciling disconnected billing systems, we create long-term financial sustainability for critical services.
              </p>
              <a href="#contact" className="text-[#FF6B35] font-medium flex items-center gap-2 hover:gap-4 transition-all cursor-pointer whitespace-nowrap text-sm md:text-base">
                Learn More <i className="ri-arrow-right-line"></i>
              </a>
            </div>

            <div className="border-l-4 border-[#FF6B35] pl-6 md:pl-8 lg:pl-10 pr-4 md:pr-6 py-6 md:py-8 lg:py-10 hover:shadow-xl transition-all hover:-translate-y-2 bg-white rounded-r-xl">
              <div className="w-12 h-12 md:w-16 md:h-16 flex items-center justify-center mb-4 md:mb-6">
                <i className="ri-scales-line text-4xl md:text-5xl lg:text-6xl text-[#FF6B35]"></i>
              </div>
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-full border-2 border-[#FF6B35] flex items-center justify-center mb-3 md:mb-4">
                <span className="text-[#FF6B35] font-bold text-sm md:text-base">02</span>
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-[#000080] mb-2 md:mb-3">Operational Equity</h3>
              <div className="text-xs md:text-sm text-[#708090] uppercase tracking-wider mb-4 md:mb-6 font-semibold">DEMOCRATIZING INTELLIGENCE</div>
              <p className="text-sm md:text-base text-[#708090] leading-relaxed mb-6 md:mb-8">
                Advanced technology should not be a luxury reserved for enterprise giants. We equip underserved sectors with enterprise-grade data intelligence, leveling the playing field and giving small agencies the visibility they need to compete and thrive.
              </p>
              <a href="#contact" className="text-[#FF6B35] font-medium flex items-center gap-2 hover:gap-4 transition-all cursor-pointer whitespace-nowrap text-sm md:text-base">
                Learn More <i className="ri-arrow-right-line"></i>
              </a>
            </div>

            <div className="border-l-4 border-[#FF6B35] pl-6 md:pl-8 lg:pl-10 pr-4 md:pr-6 py-6 md:py-8 lg:py-10 hover:shadow-xl transition-all hover:-translate-y-2 bg-white rounded-r-xl">
              <div className="w-12 h-12 md:w-16 md:h-16 flex items-center justify-center mb-4 md:mb-6">
                <i className="ri-shield-check-line text-4xl md:text-5xl lg:text-6xl text-[#FF6B35]"></i>
              </div>
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-full border-2 border-[#FF6B35] flex items-center justify-center mb-3 md:mb-4">
                <span className="text-[#FF6B35] font-bold text-sm md:text-base">03</span>
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-[#000080] mb-2 md:mb-3">Regulatory Resilience</h3>
              <div className="text-xs md:text-sm text-[#708090] uppercase tracking-wider mb-4 md:mb-6 font-semibold">FROM LIABILITY TO STRENGTH</div>
              <p className="text-sm md:text-base text-[#708090] leading-relaxed mb-6 md:mb-8">
                Government mandates are often viewed as a burden. We transform compliance from a liability into a strength. By aligning your operational workflows with state and federal regulations, we reduce administrative risk so you can focus on your core mission: people.
              </p>
              <a href="#contact" className="text-[#FF6B35] font-medium flex items-center gap-2 hover:gap-4 transition-all cursor-pointer whitespace-nowrap text-sm md:text-base">
                Learn More <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* About Section - Mission & Vision */}
      <section id="about" className="bg-[#FAFAF8] py-16 md:py-24 lg:py-32 scroll-mt-20 md:scroll-mt-24 lg:scroll-mt-28">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 lg:gap-16 items-start">
            <div>
              <div className="text-[#708090] text-xs md:text-sm tracking-wider mb-8 md:mb-12">ABOUT DATAVERGE SOLUTIONS</div>
              <p className="text-2xl md:text-3xl lg:text-4xl text-[#000080] leading-relaxed font-light">
                DataVerge Solutions is not merely another service provider; we are a new category of technology-enabled professional services firm. Headquartered in Raleigh, North Carolina, we are positioned at the intersection of technology, healthcare, and social impact.
              </p>
            </div>

            <div className="space-y-6 md:space-y-8">
              <div className="bg-[#000080] rounded-2xl p-8 md:p-10 lg:p-12 shadow-xl border-b-4 border-[#FF6B35]">
                <h3 className="text-xl md:text-2xl text-white font-bold mb-3 md:mb-4">OUR MISSION</h3>
                <p className="text-white text-base md:text-lg leading-relaxed">
                  To empower healthcare organizations and businesses with advanced data-driven solutions and expert professional services, fostering efficiency, compliance, and decisive financial control.
                </p>
              </div>

              <div className="bg-white border-2 border-[#FF6B35] rounded-2xl p-8 md:p-10 lg:p-12 shadow-xl">
                <h3 className="text-xl md:text-2xl text-[#000080] font-bold mb-3 md:mb-4">OUR VISION</h3>
                <p className="text-[#000080] text-base md:text-lg leading-relaxed">
                  To be the premier provider of integrated operational services, renowned for innovation, reliability, and transformative impact across diverse industries.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values Grid */}
      <section className="bg-[#708090] py-16 md:py-24 lg:py-32">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 lg:px-20">
          <div className="text-center mb-12 md:mb-16 lg:mb-20">
            <div className="text-white text-xs md:text-sm uppercase tracking-widest mb-4 md:mb-6">OUR VALUES</div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white">Innovation with Purpose</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-4 md:mb-6">
            <div className="bg-white rounded-2xl p-8 md:p-10 lg:p-12 hover:scale-105 transition-transform border-t-4 border-[#FF6B35]">
              <div className="w-12 h-12 flex items-center justify-center mb-4 md:mb-6">
                <i className="ri-lightbulb-line text-4xl md:text-5xl text-[#FF6B35]"></i>
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-[#000080] mb-3 md:mb-4">Innovation</h3>
              <p className="text-sm md:text-base text-[#708090] leading-relaxed">
                We do not accept the status quo. We continuously apply advanced technologies to solve real-world business problems.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 md:p-10 lg:p-12 hover:scale-105 transition-transform border-t-4 border-[#FF6B35]">
              <div className="w-12 h-12 flex items-center justify-center mb-4 md:mb-6">
                <i className="ri-hand-heart-line text-4xl md:text-5xl text-[#FF6B35]"></i>
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-[#000080] mb-3 md:mb-4">Integrity</h3>
              <p className="text-sm md:text-base text-[#708090] leading-relaxed">
                Trust is our currency. We uphold the highest standards of ethics and compliance in every client engagement.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 md:p-10 lg:p-12 hover:scale-105 transition-transform border-t-4 border-[#FF6B35]">
              <div className="w-12 h-12 flex items-center justify-center mb-4 md:mb-6">
                <i className="ri-team-line text-4xl md:text-5xl text-[#FF6B35]"></i>
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-[#000080] mb-3 md:mb-4">Radical Partnership</h3>
              <p className="text-sm md:text-base text-[#708090] leading-relaxed">
                We succeed only when our clients succeed. We are not just a vendor; we are a partner in your operational health.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            <div className="bg-white rounded-2xl p-8 md:p-10 lg:p-12 hover:scale-105 transition-transform border-t-4 border-[#FF6B35]">
              <div className="w-12 h-12 flex items-center justify-center mb-4 md:mb-6">
                <i className="ri-links-line text-4xl md:text-5xl text-[#FF6B35]"></i>
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-[#000080] mb-3 md:mb-4">Intelligent Integration</h3>
              <p className="text-sm md:text-base text-[#708090] leading-relaxed">
                The greatest value lies in breaking down silos. Our purpose is to unify systems to tell a single, accurate story.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 md:p-10 lg:p-12 hover:scale-105 transition-transform border-t-4 border-[#FF6B35]">
              <div className="w-12 h-12 flex items-center justify-center mb-4 md:mb-6">
                <i className="ri-star-line text-4xl md:text-5xl text-[#FF6B35]"></i>
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-[#000080] mb-3 md:mb-4">Excellence</h3>
              <p className="text-sm md:text-base text-[#708090] leading-relaxed">
                We are committed to delivering measurable results that create a clear, undeniable return on investment.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Industry Insights Section */}
      <section id="insights" className="bg-white py-16 md:py-24 lg:py-32 scroll-mt-20 md:scroll-mt-24 lg:scroll-mt-28">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 lg:px-20">
          <div className="mb-12 md:mb-16">
            <div className="inline-block bg-[#FF6B35] text-white px-4 md:px-6 py-2 rounded-lg text-xs md:text-sm font-semibold mb-6 md:mb-8">
              INSIGHTS
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#000080] mb-4 md:mb-6 max-w-4xl">
              Why "Underserved" Does Not Mean "Unsophisticated"
            </h2>
            <p className="text-base md:text-lg lg:text-xl text-[#708090] leading-relaxed max-w-relaxed">
              The term "underserved" often refers to a lack of access to capital or technology, not a lack of complexity.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 md:gap-12">
            <div className="lg:col-span-2">
              <div className="border-l-4 border-[#FF6B35] pl-6 md:pl-8">
                <p className="text-xl md:text-2xl lg:text-3xl italic text-[#000080] leading-relaxed">
                  "Home Health Care is one of the most complex operational environments in the economy"
                </p>
              </div>
            </div>

            <div className="lg:col-span-3 space-y-4 md:space-y-6">
              <p className="text-base md:text-lg text-[#708090] leading-loose">
                In fact, Home Health Care is one of the most complex operational environments in the economy, involving mobile workforces, strict federal compliance, and variable payer rates. These sectors require tools that are <strong className="text-[#FF6B35]">more robust, not less</strong>, than their corporate counterparts.
              </p>
              <p className="text-base md:text-lg text-[#708090] leading-loose">
                DataVerge Solutions was founded to change the narrative for underserved industries. We believe that local home care agencies and community providers deserve the same world-class operational tools used by major hospital systems and Fortune 500 companies.
              </p>
              <p className="text-base md:text-lg text-[#708090] leading-loose">
                This fragmentation leads directly to <strong className="text-[#FF6B35]">Data Blindness</strong>, the inability to see costly errors until it is too late. The result? Increased compliance risk, significant revenue leakage, and costly manual reconciliation that drains resources away from care.
              </p>
              <p className="text-base md:text-lg text-[#708090] leading-loose font-semibold">
                The Reality: You cannot manage what you cannot measure. When your data is fragmented, your financial stability is at risk.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="bg-gradient-to-b from-[#F8F9FA] to-[#E9ECEF] py-16 md:py-24 lg:py-32 scroll-mt-20 md:scroll-mt-24 lg:scroll-mt-28">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-12 lg:gap-16">
            <div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#000080] mb-6 md:mb-8">
                Let's Build Something Together
              </h2>
              <p className="text-base md:text-lg text-[#708090] mb-8 md:mb-12">Contact DataVerge Solutions</p>

              <div className="space-y-6 md:space-y-8">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-xl md:text-2xl text-[#FF6B35]"></i>
                  </div>
                  <div>
                    <div className="text-xs uppercase text-[#708090] mb-2 tracking-wider">ADDRESS</div>
                    <div className="text-base md:text-lg text-[#000080]">
                      5306 Six Forks Rd, Ste 107 #1116<br />
                      Raleigh, NC 27609
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-xl md:text-2xl text-[#FF6B35]"></i>
                  </div>
                  <div>
                    <div className="text-xs uppercase text-[#708090] mb-2 tracking-wider">PHONE</div>
                    <div className="text-base md:text-lg text-[#000080]">919-275-0574</div>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-xl md:text-2xl text-[#FF6B35]"></i>
                  </div>
                  <div>
                    <div className="text-xs uppercase text-[#708090] mb-2 tracking-wider">EMAIL</div>
                    <div className="text-base md:text-lg text-[#000080]">Info@datavergesolutions.com</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 md:p-8 lg:p-10 shadow-xl border-t-4 border-[#FF6B35]">
              <form id="contact-form" data-readdy-form onSubmit={handleSubmit}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs md:text-sm uppercase text-[#708090] mb-2 tracking-wider">Full Name</label>
                    <input
                      type="text"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      required
                      className="w-full h-10 md:h-12 px-4 border border-gray-300 rounded-lg focus:outline-none focus:border-[#FF6B35] transition-colors text-sm md:text-base"
                    />
                  </div>

                  <div>
                    <label className="block text-xs md:text-sm uppercase text-[#708090] mb-2 tracking-wider">Agency / Organization Name</label>
                    <input
                      type="text"
                      name="organization"
                      value={formData.organization}
                      onChange={handleInputChange}
                      required
                      className="w-full h-10 md:h-12 px-4 border border-gray-300 rounded-lg focus:outline-none focus:border-[#FF6B35] transition-colors text-sm md:text-base"
                    />
                  </div>

                  <div>
                    <label className="block text-xs md:text-sm uppercase text-[#708090] mb-2 tracking-wider">Email Address</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full h-10 md:h-12 px-4 border border-gray-300 rounded-lg focus:outline-none focus:border-[#FF6B35] transition-colors text-sm md:text-base"
                    />
                  </div>

                  <div>
                    <label className="block text-xs md:text-sm uppercase text-[#708090] mb-2 tracking-wider">Phone Number</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full h-10 md:h-12 px-4 border border-gray-300 rounded-lg focus:outline-none focus:border-[#FF6B35] transition-colors text-sm md:text-base"
                    />
                  </div>

                  <div>
                    <label className="block text-xs md:text-sm uppercase text-[#708090] mb-2 tracking-wider">How can we help you?</label>
                    <select
                      name="inquiry"
                      value={formData.inquiry}
                      onChange={handleInputChange}
                      required
                      className="w-full h-10 md:h-12 px-4 border border-gray-300 rounded-lg focus:outline-none focus:border-[#FF6B35] transition-colors appearance-none bg-white cursor-pointer text-sm md:text-base"
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23FF6B35' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 1rem center'
                      }}
                    >
                      <option value="">Select an option</option>
                      <option value="Operational Assessment">Operational Assessment</option>
                      <option value="Revenue Integrity">Revenue Integrity</option>
                      <option value="Compliance Support">Compliance Support</option>
                      <option value="General Inquiry">General Inquiry</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs md:text-sm uppercase text-[#708090] mb-2 tracking-wider">Message</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-[#FF6B35] transition-colors resize-none text-sm md:text-base"
                    ></textarea>
                    <div className="text-xs text-[#708090] mt-1 text-right">
                      {formData.message.length}/500 characters
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full h-12 md:h-14 bg-[#FF6B35] text-white rounded-lg hover:bg-[#000080] transition-all font-medium text-sm md:text-base flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap shadow-lg hover:shadow-xl"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                    <i className="ri-arrow-right-line"></i>
                  </button>

                  {submitStatus === 'success' && (
                    <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-xs md:text-sm">
                      Thank you! Your message has been sent successfully.
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-xs md:text-sm">
                      Sorry, there was an error sending your message. Please try again.
                    </div>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#708090] py-12 md:py-16">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid grid-cols-1 md:grid-cols-10 gap-8 md:gap-12 mb-8 md:mb-12">
            <div className="md:col-span-6">
              <Link to="/" className="inline-block cursor-pointer">
                <img 
                  src="https://static.readdy.ai/image/a2144585b096b0c5bc2cbabd4c2b6ea8/e662b63e618aedfff5c098ee5ba35e0a.png" 
                  alt="DataVerge Solutions Logo" 
                  className="h-20 md:h-28 lg:h-32 w-auto mb-6 md:mb-8"
                />
              </Link>
              <p className="text-sm md:text-base text-white mb-6 md:mb-8">Empowering the Underserved.</p>
              <p className="text-xs md:text-sm text-white/80 leading-relaxed max-w-sm mb-8 md:mb-12">
                A technology-enabled professional services firm delivering enterprise-grade operational intelligence to mission-driven organizations across essential industries.
              </p>
            </div>

            <div className="md:col-span-4 space-y-6 md:space-y-10">
              <div>
                <div className="text-xs uppercase text-white/70 mb-2 md:mb-3 tracking-wider">ADDRESS</div>
                <div className="text-sm md:text-base text-white leading-relaxed">
                  5306 Six Forks Rd, Ste 107 #1116<br />
                  Raleigh, NC 27609
                </div>
              </div>

              <div>
                <div className="text-xs uppercase text-white/70 mb-2 md:mb-3 tracking-wider">PHONE</div>
                <div className="text-sm md:text-base text-white">919-275-0574</div>
              </div>

              <div>
                <div className="text-xs uppercase text-white/70 mb-2 md:mb-3 tracking-wider">EMAIL</div>
                <div className="text-sm md:text-base text-white">Info@datavergesolutions.com</div>
              </div>
            </div>
          </div>

          <div className="border-t border-white/20 pt-6 md:pt-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="text-xs md:text-sm text-white/80 text-center md:text-left">
                © 2025 DataVerge Solutions LLC. All rights reserved.
              </div>
              <div className="flex items-center gap-4 md:gap-8">
                <Link to="/privacy-policy" className="text-xs md:text-sm text-white/80 hover:text-white transition-colors cursor-pointer">Privacy Policy</Link>
                <Link to="/terms" className="text-xs md:text-sm text-white/80 hover:text-white transition-colors cursor-pointer">Terms</Link>
                <Link to="/accessibility" className="text-xs md:text-sm text-white/80 hover:text-white transition-colors cursor-pointer">Accessibility</Link>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
